import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Paper,
  CircularProgress,
} from "@mui/material";
import { button, icon } from "../style";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../api";
import { toast } from "react-toastify";

function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value)}%`;
}

const CourseReport = () => {
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [academicYears, setAcademicYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [academicYearId, setAcademicYearId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [assignmentId, setAssignmentId] = useState("");

  const [summary, setSummary] = useState({
    total_enrolled: 0,
    completion_rate_percent: null,
    avg_test_score_percent: null,
    assignment_submitted_percent: null,
  });

  const loadFilters = useCallback(async (params = {}) => {
    try {
      setLoadingFilters(true);
      const res = await api.get("admin/course-reports/filters", { params });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load filters");
        return;
      }
      const d = res.data.data || {};
      setAcademicYears(d.academic_years || []);
      setCourses(d.courses || []);
      setSubjects(d.subjects || []);
      setAssignments(d.assignments || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load filters");
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const params = {};
      if (academicYearId) params.academic_year_id = academicYearId;
      if (courseId) params.course_id = courseId;
      if (subjectId) params.subject_id = subjectId;
      if (assignmentId) params.assignment_id = assignmentId;

      const res = await api.get("admin/course-reports/summary", { params });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load report");
        return;
      }
      setSummary(res.data.data || {});
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load report");
    } finally {
      setLoadingSummary(false);
    }
  }, [academicYearId, courseId, subjectId, assignmentId]);

  useEffect(() => {
    const params = {};
    if (academicYearId) params.academic_year_id = academicYearId;
    if (courseId) params.course_id = courseId;
    loadFilters(params);
  }, [academicYearId, courseId, loadFilters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    setAssignmentId("");
  }, [academicYearId, courseId]);

  const metrics = [
    { title: "Total enrolled", value: String(summary.total_enrolled ?? 0) },
    { title: "Completion rate", value: formatPercent(summary.completion_rate_percent) },
    { title: "Avg test score", value: formatPercent(summary.avg_test_score_percent) },
    { title: "Assignment submitted", value: formatPercent(summary.assignment_submitted_percent) },
  ];

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3} fontWeight={700}>
        Course Report
      </Typography>

      {loadingFilters ? (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading filters…
          </Typography>
        </Box>
      ) : null}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="Academic year"
            value={academicYearId}
            onChange={(e) => {
              setAcademicYearId(e.target.value);
              setCourseId("");
            }}
            disabled={loadingFilters}
          >
            <MenuItem value="">
              <em>All years</em>
            </MenuItem>
            {academicYears.map((y) => (
              <MenuItem key={y.id} value={String(y.id)}>
                {y.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="Course"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            disabled={loadingFilters}
          >
            <MenuItem value="">
              <em>All courses</em>
            </MenuItem>
            {courses.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="Subject"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            disabled={loadingFilters}
          >
            <MenuItem value="">
              <em>All subjects</em>
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="Assignment"
            value={assignmentId}
            onChange={(e) => setAssignmentId(e.target.value)}
            disabled={loadingFilters || !academicYearId || !courseId || assignments.length === 0}
            helperText={
              !academicYearId || !courseId
                ? "Select academic year and course to list assignments"
                : assignments.length === 0
                  ? "No assignments for this course/year"
                  : ""
            }
          >
            <MenuItem value="">
              <em>All assignments</em>
            </MenuItem>
            {assignments.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Box mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            const params = {};
            if (academicYearId) params.academic_year_id = academicYearId;
            if (courseId) params.course_id = courseId;
            loadFilters(params);
            loadSummary();
          }}
          disabled={loadingSummary}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2}>
        {metrics.map((metric, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center", minHeight: 120 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {metric.title}
              </Typography>
              {loadingSummary ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Typography variant="h4" component="div">
                  {metric.value}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button
          disableElevation
          sx={button}
          onClick={() =>
            toast.info("Export will be available in a future update.", { autoClose: 3000 })
          }
        >
          Export as PDF
          <Box sx={icon}>
            <ArrowForwardIcon sx={{ fontSize: 20, color: "#EF2A1E" }} />
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

export default CourseReport;
